import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import { 
  HelpCircle, FileText, Shield, Store, Building2, Users,
  ChevronDown, ChevronRight, Search, MessageSquare, ExternalLink
} from 'lucide-react';

const SupportHubPage = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedPolicy, setExpandedPolicy] = useState(null);

  const policies = [
    {
      id: 'usage',
      icon: Users,
      title: 'Playmatez Usage Policy',
      description: 'Guidelines for using the Playmatez platform',
      sections: [
        {
          title: 'Account Guidelines',
          content: 'Users must provide accurate information during registration. Each user may only have one account. Sharing account credentials is prohibited. Users must be 13 years or older to use the platform.',
        },
        {
          title: 'Community Standards',
          content: 'Treat all users with respect. No harassment, hate speech, or discriminatory behavior. Report inappropriate content or behavior. Keep communications professional and sports-related.',
        },
        {
          title: 'Content Policy',
          content: 'Only post content relevant to sports and networking. No explicit, violent, or illegal content. Respect intellectual property rights. Do not spam or post misleading information.',
        },
        {
          title: 'Safety Guidelines',
          content: 'Meet in public places for first-time meetups. Verify identities through the platform. Report suspicious behavior immediately. Never share sensitive personal information publicly.',
        },
      ],
    },
    {
      id: 'venue',
      icon: Building2,
      title: 'Venue Policy',
      description: 'Terms and conditions for venue listings',
      sections: [
        {
          title: 'Listing Requirements',
          content: 'Venues must provide accurate facility information. Photos must represent the actual venue. Pricing must be transparent with no hidden fees. Operating hours must be kept up to date.',
        },
        {
          title: 'Booking & Cancellation',
          content: 'Venues must honor confirmed bookings. Cancellation policies must be clearly stated. Refunds should be processed within 7 business days. Emergency cancellations must be communicated immediately.',
        },
        {
          title: 'Quality Standards',
          content: 'Maintain clean and safe facilities. Ensure equipment is in good working condition. Provide adequate lighting and ventilation. Have first-aid facilities available.',
        },
        {
          title: 'Reviews & Ratings',
          content: 'Venues can respond to reviews professionally. Fake reviews are prohibited. Disputes can be reported to Playmatez support. Ratings reflect genuine user experiences.',
        },
      ],
    },
    {
      id: 'marketplace',
      icon: Store,
      title: 'Marketplace Policy',
      description: 'Rules for buying and selling sports goods',
      sections: [
        {
          title: 'Listing Guidelines',
          content: 'Only sports-related items can be listed. Provide accurate descriptions and photos. State item condition clearly (new, used, etc.). Price items fairly and transparently.',
        },
        {
          title: 'Prohibited Items',
          content: 'No counterfeit or replica items. No weapons or dangerous equipment. No stolen goods. No items that violate local laws.',
        },
        {
          title: 'Transactions',
          content: 'Use secure payment methods. Meet in safe, public locations for exchanges. Verify item condition before completing purchase. Report fraudulent listings immediately.',
        },
        {
          title: 'Seller Responsibilities',
          content: 'Ship items within stated timeframe. Package items securely. Provide tracking information when available. Handle returns as per stated policy.',
        },
      ],
    },
    {
      id: 'crew',
      icon: Users,
      title: 'Professional Crew Policy',
      description: 'Guidelines for coaches, referees, and trainers',
      sections: [
        {
          title: 'Verification Requirements',
          content: 'Provide valid credentials and certifications. Undergo background verification if required. Maintain professional qualifications. Update certifications as they renew.',
        },
        {
          title: 'Service Standards',
          content: 'Deliver services as described. Maintain punctuality for appointments. Provide professional conduct at all times. Respect client confidentiality.',
        },
        {
          title: 'Booking Management',
          content: 'Keep availability calendar updated. Respond to inquiries within 24 hours. Honor confirmed appointments. Communicate cancellations promptly.',
        },
        {
          title: 'Payment & Fees',
          content: 'Set clear pricing for services. Platform fees apply to transactions. Payments processed within 3-5 business days. Disputes handled through support.',
        },
      ],
    },
    {
      id: 'data',
      icon: Shield,
      title: 'Data & Privacy Policy',
      description: 'How we handle your personal information',
      sections: [
        {
          title: 'Data Collection',
          content: 'We collect information you provide during registration. Usage data helps improve our services. Location data enables local features. We do not sell personal data to third parties.',
        },
        {
          title: 'Data Usage',
          content: 'Personal data is used to provide our services. Analytics help us improve user experience. Communication data enables notifications. Aggregated data may be used for research.',
        },
        {
          title: 'Data Protection',
          content: 'Industry-standard encryption protects your data. Regular security audits are conducted. Access to data is strictly controlled. We comply with applicable data protection laws.',
        },
        {
          title: 'Your Rights',
          content: 'Request access to your personal data. Request correction of inaccurate data. Request deletion of your data. Opt-out of marketing communications.',
        },
      ],
    },
    {
      id: 'community',
      icon: MessageSquare,
      title: 'Community Guidelines',
      description: 'Building a positive sports community',
      sections: [
        {
          title: 'Respectful Interaction',
          content: 'Treat all community members with respect. No bullying, harassment, or intimidation. Respect diverse backgrounds and abilities. Encourage and support fellow sports enthusiasts.',
        },
        {
          title: 'Fair Play',
          content: 'Honor commitments and show up to games. Play by the rules of the sport. Accept wins and losses gracefully. Report cheating or unfair behavior.',
        },
        {
          title: 'Reporting & Enforcement',
          content: 'Report violations through the app. Reports are reviewed within 48 hours. Violations may result in warnings or bans. Appeals can be submitted to support.',
        },
        {
          title: 'Positive Contribution',
          content: 'Share helpful tips and experiences. Welcome newcomers to the community. Organize inclusive events. Promote sportsmanship and healthy competition.',
        },
      ],
    },
  ];

  const faqs = [
    {
      question: 'How do I create an account?',
      answer: 'Download the Playmatez app from Google Play or App Store, then follow the registration process. You can sign up using your email or phone number.',
    },
    {
      question: 'How do I find players near me?',
      answer: 'Use the search feature with location filters to find players in your area. You can filter by sport, skill level, and availability.',
    },
    {
      question: 'How do I list my venue?',
      answer: 'Go to Partner with Us section, select Venue, and fill out the partnership application. Our team will verify your listing within 48 hours.',
    },
    {
      question: 'How do I report inappropriate behavior?',
      answer: 'Use the report button on any profile or content, or contact us at connect@playmatez.io with details of the incident.',
    },
    {
      question: 'How do I delete my account?',
      answer: 'Go to Settings > Account > Delete Account in the app, or email us at connect@playmatez.io requesting account deletion.',
    },
  ];

  const filteredPolicies = policies.filter(
    policy =>
      policy.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      policy.description.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Navigation />
      
      {/* Hero Section */}
      <section className="pt-32 pb-16 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-primary/5 via-transparent to-transparent" />
        <div className="container-custom relative z-10">
          <motion.div
            className="text-center max-w-3xl mx-auto"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <p className="text-xs font-bold tracking-[0.2em] uppercase text-primary mb-4">
              Support Hub
            </p>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight text-white mb-6">
              How Can We <span className="gradient-text">Help?</span>
            </h1>
            <p className="text-muted-foreground text-lg mb-8">
              Find answers to common questions, read our policies, and get the support you need.
            </p>

            {/* Search */}
            <div className="relative max-w-xl mx-auto">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
              <input
                type="text"
                placeholder="Search policies and help articles..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-12 pr-4 py-4 bg-card border border-white/10 rounded-xl text-white placeholder-muted-foreground focus:outline-none focus:border-primary transition-colors"
                data-testid="support-search"
              />
            </div>
          </motion.div>
        </div>
      </section>

      {/* Quick Links */}
      <section className="py-8">
        <div className="container-custom">
          <div className="flex flex-wrap items-center justify-center gap-4">
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 px-4 py-2 bg-card border border-white/10 rounded-full text-sm text-muted-foreground hover:text-white hover:border-primary transition-colors"
            >
              <MessageSquare className="w-4 h-4" />
              Contact Support
            </Link>
            <Link
              to="/privacy-policy"
              className="inline-flex items-center gap-2 px-4 py-2 bg-card border border-white/10 rounded-full text-sm text-muted-foreground hover:text-white hover:border-primary transition-colors"
            >
              <Shield className="w-4 h-4" />
              Privacy Policy
            </Link>
            <Link
              to="/terms"
              className="inline-flex items-center gap-2 px-4 py-2 bg-card border border-white/10 rounded-full text-sm text-muted-foreground hover:text-white hover:border-primary transition-colors"
            >
              <FileText className="w-4 h-4" />
              Terms & Conditions
            </Link>
          </div>
        </div>
      </section>

      {/* Policies Grid */}
      <section className="py-12">
        <div className="container-custom">
          <h2 className="font-heading text-2xl font-bold text-white mb-8">
            Policies & Guidelines
          </h2>
          <div className="space-y-4">
            {filteredPolicies.map((policy) => {
              const Icon = policy.icon;
              const isExpanded = expandedPolicy === policy.id;
              
              return (
                <motion.div
                  key={policy.id}
                  className="bg-card border border-white/10 rounded-2xl overflow-hidden"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                >
                  <button
                    onClick={() => setExpandedPolicy(isExpanded ? null : policy.id)}
                    className="w-full p-6 flex items-center gap-4 text-left hover:bg-white/5 transition-colors"
                    data-testid={`policy-${policy.id}`}
                  >
                    <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <Icon className="w-6 h-6 text-primary" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-white">{policy.title}</h3>
                      <p className="text-muted-foreground text-sm">{policy.description}</p>
                    </div>
                    <ChevronDown
                      className={`w-5 h-5 text-muted-foreground transition-transform ${
                        isExpanded ? 'rotate-180' : ''
                      }`}
                    />
                  </button>
                  
                  {isExpanded && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      className="px-6 pb-6"
                    >
                      <div className="pt-4 border-t border-white/10 space-y-6">
                        {policy.sections.map((section, index) => (
                          <div key={index}>
                            <h4 className="font-semibold text-white mb-2">{section.title}</h4>
                            <p className="text-muted-foreground text-sm leading-relaxed">
                              {section.content}
                            </p>
                          </div>
                        ))}
                      </div>
                    </motion.div>
                  )}
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-12 bg-card/30">
        <div className="container-custom">
          <h2 className="font-heading text-2xl font-bold text-white mb-8">
            Frequently Asked Questions
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {faqs.map((faq, index) => (
              <motion.div
                key={index}
                className="p-6 bg-card rounded-xl border border-white/10"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <div className="flex items-start gap-3">
                  <HelpCircle className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                  <div>
                    <h3 className="font-semibold text-white mb-2">{faq.question}</h3>
                    <p className="text-muted-foreground text-sm">{faq.answer}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact CTA */}
      <section className="py-16">
        <div className="container-custom text-center">
          <h2 className="font-heading text-2xl font-bold text-white mb-4">
            Still Need Help?
          </h2>
          <p className="text-muted-foreground mb-6">
            Our support team is here to assist you with any questions or concerns.
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center gap-2 px-6 py-3 bg-primary text-white font-semibold rounded-full hover:bg-primary/90 transition-colors"
            data-testid="support-contact-btn"
          >
            Contact Support
            <ChevronRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default SupportHubPage;
